import { useEffect, useState } from 'react'
import { AlertErrorCircle, Document, Folder } from '@/assets/icons'
import { Modal, ModalContent, ModalFooterWithActions } from '@/components/modal'
import { kUserFilesFileNameMaxLength, kUserFilesFolderNameMaxLength } from '@/user_files/limits'
import { hasInvalidUserFilesNameCharacter, kInvalidUserFilesNameMessage } from '@/user_files/utils/name-validation'
import { splitFilename } from '@/user_files/utils/filename'

export type Conflict = {
  originalName: string
  resolvedName: string
  type?: 'file' | 'folder'
}

type ConflictModalProps = {
  /** The conflicts to display in the modal. */
  conflicts: Conflict[]
  /** Whether this modal is for a move or upload operation. */
  operation: 'move' | 'upload'
  /** The error to display in the footer. */
  error?: string | null
  /** Whether the modal is currently submitting. */
  isSubmitting?: boolean
  /** The names that are already occupied in the folder. */
  occupiedNames: ReadonlySet<string>
  /** The function to call when the user cancels the modal. */
  onCancel: () => void
  /** The function to call when the user confirms the modal. */
  onConfirm: (resolvedNames: string[]) => void
  /** Whether the modal is open. */
  open: boolean
}

export function ConflictModal({ open, conflicts, operation, occupiedNames, onConfirm, onCancel, isSubmitting = false, error }: ConflictModalProps) {
  const fieldLabel = operation === 'upload' ? 'Save upload as' : 'Rename to'
  const primaryActionLabel = operation === 'upload' ? 'Upload all' : 'Rename and move'
  const secondaryActionLabel = operation === 'upload' ? 'Cancel all' : 'Cancel'
  const [names, setNames] = useState<string[]>([])

  const exts = conflicts.map((c) => (c.type === 'folder' ? '' : splitFilename(c.resolvedName).ext))

  useEffect(() => {
    if (open) {
      setNames(conflicts.map((c) => (c.type === 'folder' ? c.resolvedName : splitFilename(c.resolvedName).base)))
    }
  }, [open, conflicts])

  const updateName = (index: number, value: string) => {
    setNames((prev) => {
      const next = [...prev]
      next[index] = value
      return next
    })
  }

  const getError = (name: string, index: number): string | null => {
    const trimmed = name.trim()
    if (!trimmed) return 'Name is required'

    const fullName = trimmed + exts[index]
    if (hasInvalidUserFilesNameCharacter(fullName)) return kInvalidUserFilesNameMessage
    if (occupiedNames.has(fullName)) {
      return conflicts[index].type === 'folder'
        ? 'A folder with this name already exists in this location'
        : 'A file with this name already exists in this location'
    }

    const duplicate = names.some((n, i) => i !== index && n.trim() + exts[i] === fullName)
    if (duplicate) return 'Each item must have a unique name'
    return null
  }

  const errors = names.map((n, i) => getError(n, i))
  const count = conflicts.length
  const canConfirm = names.length === conflicts.length && errors.every((e) => e === null)

  const allFolders = conflicts.every((c) => c.type === 'folder')
  const allFiles = conflicts.every((c) => c.type !== 'folder')
  const title =
    count === 1
      ? allFolders
        ? 'Folder already exists in this location'
        : 'File already exists in this location'
      : allFolders
      ? `${count} folders already exist in this location`
      : allFiles
      ? `${count} files already exist in this location`
      : `${count} items already exist in this location`

  let subtitle: string
  if (operation === 'upload') {
    subtitle =
      count === 1
        ? 'Each file must have a unique name. Rename your upload below, or delete the existing file first to replace it.'
        : 'Each file must have a unique name. Rename your uploads below, or delete the existing files first to replace them.'
  } else {
    subtitle =
      count === 1
        ? allFolders
          ? 'A folder with this name already exists in the destination folder. Rename it below before moving.'
          : 'A file with this name already exists in the destination folder. Rename it below before moving.'
        : allFolders
        ? 'Folders with these names already exist in the destination folder. Rename them below before moving.'
        : allFiles
        ? 'Files with these names already exist in the destination folder. Rename them below before moving.'
        : 'Items with these names already exist in the destination folder. Rename them below before moving.'
  }

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel()
      }}
    >
      <ModalContent
        title={title}
        subtitle={subtitle}
        width="md"
        height="max-full"
        footer={
          <ModalFooterWithActions
            inlineMessage={error ? { variant: 'error', children: error } : undefined}
            secondaryAction={{ label: secondaryActionLabel, onClick: onCancel, disabled: isSubmitting }}
            primaryAction={{
              label: primaryActionLabel,
              onClick: () => onConfirm(names.map((n, i) => n.trim() + exts[i])),
              disabled: !canConfirm || isSubmitting,
              loading: isSubmitting,
            }}
          />
        }
      >
        <div className="border border-brand-neutral-200 rounded-lg overflow-y-auto max-h-72">
          {conflicts.map((conflict, i) => (
            <div key={`${conflict.originalName}-id-${i}`} className="border-t border-brand-neutral-200 first:border-t-0 px-4 pt-0 pb-4">
              <div className="flex items-center gap-[10px] py-4">
                {conflict.type === 'folder' ? (
                  <Folder className="w-6 h-6 shrink-0 text-brand-neutral-950" />
                ) : (
                  <Document className="w-6 h-6 shrink-0 text-brand-neutral-950" />
                )}
                <span className="text-sm font-medium text-brand-neutral-950 truncate" title={conflict.originalName}>
                  {conflict.originalName}
                </span>
              </div>
              <div className="float-label relative">
                <input
                  id={`conflict-name-${i}`}
                  className={`w-full rounded border-0 ring-1 ring-inset focus:outline-none ${
                    errors[i] ? 'ring-red-500 focus:ring-inset focus:ring-red-500' : 'ring-brand-neutral-400 focus:ring-inset focus:ring-brand-400'
                  }`}
                  placeholder=" "
                  value={names[i] ?? ''}
                  maxLength={(conflict.type === 'folder' ? kUserFilesFolderNameMaxLength : kUserFilesFileNameMaxLength) - exts[i].length}
                  onChange={(e) => updateName(i, e.target.value)}
                  onBlur={(e) => updateName(i, e.target.value.trim())}
                />
                <label htmlFor={`conflict-name-${i}`} className="text-brand-neutral-600">
                  {fieldLabel} <span className="text-red-700">*</span>
                </label>
              </div>
              {errors[i] && (
                <div className="flex items-center gap-1 mt-1.5 text-red-700">
                  <AlertErrorCircle className="w-4 h-4 shrink-0" />
                  <span className="text-xs">{errors[i]}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </ModalContent>
    </Modal>
  )
}
